import React, { useEffect, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Avatar, Paper, Button } from '@mui/material';
import { generateImageUrlFromCDN } from '../Util';
import { executeFetch } from '../Util.js';
import { config } from '../Const.js';
import SearchToolbar from '../quizzes/SearchToolbar.jsx';

const WeeklyQuizzesQuizSelector = ({ category, assignments, selectedWeek, onSelectQuiz }) => {
    const [availableQuizList, setAvailableQuizList] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (category) {
            fetchCategoryQuizzes();
        } else {
            setAvailableQuizList([]);
        }
        // eslint-disable-next-line        
    }, [category]);

    const fetchCategoryQuizzes = () => {
        if (category === 'featured') {
            setAvailableQuizList([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        let url = config.SERVER_URL + "/api/v1/admin/quiz/filter-by-main-tag?tag=" + category;

        let responseHandler = (quizList) => {
            setAvailableQuizList(quizList);
            setLoading(false);
        };
        executeFetch(url, "get", null, null, null, responseHandler);
    };

    const handleSearch = (searchTerm) => {
        setLoading(true);

        const trimmedSearch = (searchTerm || '').trim();
        let url = config.SERVER_URL + "/api/v1/admin/quiz/with-stats";
        if (trimmedSearch.length > 0) {
            url += `?search=${encodeURIComponent(trimmedSearch)}`;
        }

        const responseHandler = (result) => {
            setAvailableQuizList(Array.isArray(result) ? result : []);
            setLoading(false);
        };
        executeFetch(url, "get", null, null, null, responseHandler);
    };

    const isQuizUsedInOtherWeek = (quizId) => {
        const currentWeekKey = selectedWeek?.format('YYYY-MM-DD');

        return Object.entries(assignments).some(([weekKey, categories]) => {
            if (weekKey === currentWeekKey) return false;
            return Object.values(categories).some(assignedQuiz => assignedQuiz && assignedQuiz.id === quizId);
        });
    };

    if (!category) {
        return (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Typography color="textSecondary">Select a category from the left to choose a quiz.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h6">
                    Choose Quiz for {category.toUpperCase()}
                </Typography>
                <SearchToolbar
                    onSearch={handleSearch}
                    onReset={fetchCategoryQuizzes}
                    loading={loading}
                    hasLoadedAnything={availableQuizList.length > 0}
                />
            </Box>
            <Paper elevation={10} sx={{ height: 'calc(100% - 120px)', overflowY: 'auto', p: 2 }}>
                {loading ? (
                    <Typography sx={{ textAlign: 'center', mt: 4 }}>Loading quizzes...</Typography>
                ) : (
                    <List>
                        {availableQuizList.map((quiz) => {
                            const used = isQuizUsedInOtherWeek(quiz.id);
                            return (
                                <ListItem
                                    key={quiz.id}
                                    sx={{
                                        mb: '8px',
                                        borderRadius: 1,
                                        border: '1px solid #eee',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        opacity: used ? 0.6 : 1,
                                        backgroundColor: used ? '#f9f9f9' : 'transparent'
                                    }}
                                    disablePadding
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 1 }}>
                                        <Avatar
                                            alt={quiz.label}
                                            src={generateImageUrlFromCDN(quiz.shareImageMetadata)}
                                            sx={{ width: 40, height: 40 }}
                                        />
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <ListItemText
                                                primary={quiz.label}
                                                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 400 }}
                                            />
                                            {used && (
                                                <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                                                    Used in another week
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => onSelectQuiz(quiz)}
                                        disabled={used}
                                        sx={{ mr: 1 }}
                                    >
                                        {used ? 'Used' : 'Select'}
                                    </Button>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </Paper>
        </Box>
    );
};

export default WeeklyQuizzesQuizSelector;
